import { useState } from "react";
import { formatDuration, STAGED_TOOL, traceSummary, type TraceRow } from "@/chat/trace";
import { Chip, StatusDot, type Tone } from "@/components/ui/Chip";
import { CheckIcon, ChevronRightIcon, DatabaseIcon, ShieldIcon, TableIcon } from "@/components/icons";
import { Spinner } from "@/components/ui/Spinner";
import { cn } from "@/lib/cn";
import { RelationLinks, SqlBlock } from "./SqlBlock";

/**
 * The tool timeline under an answer (PRD FR-5.1, FR-5.2).
 *
 * This is the element the whole product's trust claim rests on: "the number
 * came from the database, not from the model's imagination" is only credible
 * if the statement that produced it is one click away. So it renders while the
 * run is still going and stays with the stored message afterwards, from the
 * same shape either way.
 */

const TOOL_LABELS: Record<string, string> = {
  list_relations: "Read the catalog",
  get_table_definition: "Inspected",
  execute_sql: "Ran SQL",
  [STAGED_TOOL]: "Joined",
};

/**
 * How a multi-source trace names its sources (MS-4). `names` maps a stored
 * step's connection id to a display name; `ids` maps a live step's source
 * name back to an id, so its schema links still resolve. Both empty for a
 * single-source answer, whose trace is exactly what it was.
 */
export interface TraceSources {
  names: Record<string, string>;
  ids: Record<string, string>;
}

function toolIcon(tool: string, outcome: TraceRow["outcome"]) {
  if (outcome === "pending") return <Spinner className="size-3.5" />;
  if (outcome === "blocked") return <ShieldIcon className="size-3.5" />;
  if (tool === "execute_sql") return <DatabaseIcon className="size-3.5" />;
  return <TableIcon className="size-3.5" />;
}

function outcomeTone(outcome: TraceRow["outcome"]): Tone {
  if (outcome === "blocked") return "error";
  if (outcome === "error") return "warning";
  return "success";
}

function StepRow({
  row,
  connectionId,
  sources,
  anchor,
}: {
  row: TraceRow;
  connectionId?: string | null;
  sources?: TraceSources;
  anchor: string;
}) {
  const label = TOOL_LABELS[row.tool] ?? row.tool;
  const target = String(row.arguments.table_name ?? "");
  const staged = row.tool === STAGED_TOOL;
  // The source this step ran on: by id when stored, by name while live.
  const sourceName = row.connectionId
    ? (sources?.names[row.connectionId] ?? row.source)
    : row.source;
  const stepConnection = staged
    ? null
    : (row.connectionId ?? (row.source ? sources?.ids[row.source] : undefined) ?? connectionId);
  const multi = Boolean(sources && Object.keys(sources.names).length > 1);
  return (
    <li
      id={row.queryIndex !== null ? `${anchor}-q${row.queryIndex}` : undefined}
      className="border-border flex scroll-mt-4 flex-col gap-2 border-b py-2.5 last:border-0 last:pb-0"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("shrink-0", row.outcome === "blocked" ? "text-error" : "text-ink-secondary")}>
          {toolIcon(row.tool, row.outcome)}
        </span>
        {multi && sourceName && !staged ? <Chip tone="neutral">{sourceName}</Chip> : null}
        <span className="type-label text-ink">
          {label}
          {target ? <span className="text-ink-secondary"> {target}</span> : null}
          {staged && row.inputs.length ? (
            <span className="text-ink-secondary">
              {" "}
              {row.inputs.map((index, position) => (
                <span key={index}>
                  {position > 0 ? <span aria-label="joined with"> ⋈ </span> : null}
                  <a
                    href={`#${anchor}-q${index}`}
                    className="text-accent font-mono hover:underline"
                    title={`Go to query ${index}`}
                  >
                    q{index}
                  </a>
                </span>
              ))}
            </span>
          ) : null}
        </span>
        {row.queryIndex !== null ? (
          <span className="type-caption text-ink-disabled">query {row.queryIndex}</span>
        ) : null}
        {row.retryOf !== null ? <Chip tone="warning">retry of step {row.retryOf}</Chip> : null}
        <span className="flex-1" />
        {row.rowCount !== null ? (
          <span className="type-support text-ink-secondary tabular-nums">
            {row.rowCount.toLocaleString()} row{row.rowCount === 1 ? "" : "s"}
            {row.truncated ? "+" : ""}
          </span>
        ) : null}
        {row.durationMs !== null ? (
          <span className="type-caption text-ink-disabled tabular-nums">
            {formatDuration(row.durationMs)}
          </span>
        ) : null}
        <StatusDot tone={row.outcome === "pending" ? "neutral" : outcomeTone(row.outcome)} />
      </div>

      {row.sql ? (
        <div className="flex flex-col gap-1">
          {staged ? (
            <span className="type-caption text-ink-disabled">DuckDB · staged results</span>
          ) : null}
          <SqlBlock
            sql={row.sql}
            relations={row.relations}
            connectionId={stepConnection}
            tone={row.outcome === "blocked" ? "blocked" : "default"}
          />
        </div>
      ) : null}

      {row.inputsTruncated.length ? (
        <p className="bg-warning-subtle text-warning type-support rounded-element px-2.5 py-2">
          {row.inputsTruncated.map((index) => `q${index}`).join(", ")} hit its source&rsquo;s row
          cap, so this join may be missing rows.
        </p>
      ) : null}

      <RelationLinks relations={row.relations} connectionId={stepConnection} />

      {row.error ? (
        <p
          className={cn(
            "type-support rounded-element px-2.5 py-2",
            row.outcome === "blocked" ? "bg-error-subtle text-error" : "bg-warning-subtle text-warning",
          )}
        >
          {row.outcome === "blocked" ? "Blocked by the read-only guard: " : ""}
          {row.error}
        </p>
      ) : null}
    </li>
  );
}

export function TraceTimeline({
  rows,
  connectionId,
  sources,
  anchor = "trace",
  defaultOpen = false,
  live = false,
}: {
  rows: TraceRow[];
  /** Lets each statement link its relations into the schema browser (FR-5.2). */
  connectionId?: string | null;
  /** A multi-source answer's sources, to label each step (MS-4). */
  sources?: TraceSources;
  /** Unique per trace on the page, so a join's `qN` links find their step. */
  anchor?: string;
  defaultOpen?: boolean;
  live?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (rows.length === 0) return null;
  const blocked = rows.some((row) => row.outcome === "blocked");

  return (
    <div className="border-border rounded-element border">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-start",
          "type-support text-ink-secondary hover:bg-hover hover:text-ink motion-fast rounded-element",
        )}
      >
        <ChevronRightIcon className={cn("size-3.5 motion-fast", open && "rotate-90")} />
        <span>{traceSummary(rows)}</span>
        {blocked ? <Chip tone="error">write blocked</Chip> : null}
        {live ? <Spinner className="size-3.5" /> : <CheckIcon className="text-success size-3.5" />}
      </button>
      {open ? (
        <ul className="border-border flex flex-col border-t px-3 pt-1 pb-3">
          {rows.map((row) => (
            <StepRow
              key={row.seq}
              row={row}
              connectionId={connectionId}
              sources={sources}
              anchor={anchor}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
